import type { TrainingSlideDef } from '@hcc/types';

/**
 * Threaded from the player into the slide renderer so each video block can
 * report when it's been watched and read back its own state on resume. Absent
 * in builder previews, where videos play without gating.
 */
export interface WatchGate {
  isWatched: (blockId: string) => boolean;
  onWatched: (blockId: string) => void;
}

/**
 * Ids of the watch-gated video blocks on a slide, including those nested in a
 * columns block. The player blocks "Next"/"Finish" until every id here is in
 * the watched set, so a learner can't skip past an unwatched video.
 */
export function slideVideoBlockIds(slide: TrainingSlideDef): string[] {
  const ids: string[] = [];
  for (const block of slide.blocks ?? []) {
    if (block.type === 'video') {
      ids.push(block.id);
    } else if (block.type === 'columns') {
      for (const leaf of [...block.left, ...block.right]) {
        if (leaf.type === 'video') ids.push(leaf.id);
      }
    }
  }
  return ids;
}

/** Whether every gated video on the slide has been watched. */
export function slideVideosWatched(
  slide: TrainingSlideDef,
  watched: ReadonlySet<string>,
): boolean {
  return slideVideoBlockIds(slide).every((id) => watched.has(id));
}
